import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@arcadeai/design-system/components/ui/atoms/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@arcadeai/design-system/components/ui/atoms/chart";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import { Info } from "lucide-react";
import type React from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";
import type { MetricUsage } from "@/app/billing/types";

// Margin constants for different digit counts
const MARGIN_ONE_DIGIT = -30;
const MARGIN_TWO_DIGITS = -25;
const MARGIN_THREE_DIGITS = -20;
const MARGIN_FOUR_DIGITS = -15;
const MARGIN_FIVE_DIGITS = -5;
const MARGIN_DEFAULT = 0;

// Constants for digit thresholds
const DIGIT_THRESHOLD_THREE = 3;
const DIGIT_THRESHOLD_FOUR = 4;
const DIGIT_THRESHOLD_FIVE = 5;

// Constants for chart scaling
const CHART_SCALE_FACTOR = 1.1;
const CHART_MINIMUM_VALUE = 1;

type ChartDataPoint = {
  date: string;
  value: number;
  [key: string]: string | number;
};

type ChartCardProps = {
  title: string;
  description?: string;
  data: ChartDataPoint[];
  chartType?: "bar" | "line";
  dataKey?: string;
  colorIndex?: number;
  formatXAxisTick?: (value: string | number) => string;
  usageMetric?: MetricUsage;
};

const getLeftMargin = (digits: number): number => {
  if (digits === 1) {
    return MARGIN_ONE_DIGIT;
  }
  if (digits === 2) {
    return MARGIN_TWO_DIGITS;
  }
  if (digits === DIGIT_THRESHOLD_THREE) {
    return MARGIN_THREE_DIGITS;
  }
  if (digits === DIGIT_THRESHOLD_FOUR) {
    return MARGIN_FOUR_DIGITS;
  }
  if (digits === DIGIT_THRESHOLD_FIVE) {
    return MARGIN_FIVE_DIGITS;
  }
  return MARGIN_DEFAULT; // 6 or more digits
};

export const ChartCard: React.FC<ChartCardProps> = ({
  title,
  description = "Usage per day over time",
  data,
  chartType = "bar",
  dataKey = "value",
  colorIndex = 1,
  formatXAxisTick,
  usageMetric,
}) => {
  const ChartComponent = chartType === "bar" ? BarChart : AreaChart;

  const defaultXAxisFormatter = (value: string | number) =>
    new Date(value).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

  const xAxisFormatter = formatXAxisTick || defaultXAxisFormatter;

  // Check if this is MAU data based on the metric name
  const isMAUData =
    usageMetric?.billable_metric.name.toLowerCase().includes("mau") ||
    usageMetric?.billable_metric.name
      .toLowerCase()
      .includes("monthly active users");

  // Calculate dynamic left margin based on max value
  const maxValue = Math.max(...data.map((item) => Number(item[dataKey]) || 0));
  const digitCount = Math.max(
    1,
    Math.floor(Math.log10(Math.abs(maxValue))) + 1
  );
  const leftMargin = getLeftMargin(digitCount);

  // Detect empty or all-zero datasets to show a friendly empty state
  const numericValues = data.map((item) => Number(item[dataKey]) || 0);
  const hasAnyValue = numericValues.some((value) => value > 0);
  const isEmpty = data.length === 0 || !hasAnyValue;

  if (isEmpty) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg">{title}</CardTitle>
          {!!isMAUData && (
            <Tooltip>
              <TooltipTrigger className="cursor-help">
                <Info className="h-4 w-4 text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-pretty">
                  Monthly Active Users are the unique end-users{" "}
                  <span className="font-bold italic">(user_id)</span> who have
                  authorized or called a tool in your app/agent within the past
                  month.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <CardDescription className="text-muted-foreground/70 text-xs italic">
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer
          className="h-full max-h-[300px] w-full"
          config={{
            [dataKey]: {
              label: title,
              color: `var(--chart-${colorIndex})`,
            },
          }}
        >
          <ChartComponent
            accessibilityLayer
            data={data}
            margin={{ top: 10, right: 20, bottom: 0, left: leftMargin }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="date"
              minTickGap={10}
              tick={{ fontSize: 10 }}
              tickFormatter={xAxisFormatter}
              tickLine={false}
              tickMargin={12}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              domain={[
                0,
                (dataMax: number) =>
                  Math.max(
                    CHART_MINIMUM_VALUE,
                    Math.ceil(dataMax * CHART_SCALE_FACTOR)
                  ),
              ]}
              tick={{ fontSize: 10 }}
              tickLine={false}
              tickMargin={12}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className={cn("min-w-[9rem]", isMAUData && "min-w-[14rem]")}
                  indicator="line"
                  labelFormatter={(value) =>
                    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                    xAxisFormatter(value as string | number)
                  }
                />
              }
              cursor={false}
            />
            {chartType === "bar" ? (
              <Bar
                dataKey={dataKey}
                fill={`var(--color-${dataKey})`}
                radius={8}
              />
            ) : (
              <Area
                dataKey={dataKey}
                fill={`var(--color-${dataKey})`}
                fillOpacity={0.4}
                stroke={`var(--color-${dataKey})`}
                strokeWidth={2}
                type="linear"
              />
            )}
          </ChartComponent>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
